import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { Mail, FileText, Paperclip, X, Send, Loader2, CheckCircle, Plus, ShoppingCart, Trash2, Link2, Download, Film, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FOLIE, calcPrice } from "@/lib/pricing";
import StickerPreview from "@/components/calculator/StickerPreview";
import QuantityTiers from "@/components/calculator/QuantityTiers";

const MAX_FILE_SIZE = 25 * 1024 * 1024;

const VAT = 23;

const QTY_PRESETS = [1, 50, 100, 200, 300, 500];

const SHAPE_KEYS = ["rect", "round", "custom"] as const;

const fmt = (n: number) => {
  const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
  return new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rounded);
};

const DEFAULT_EUR_RATE = 4.30;

function useDynamicEurRate() {
  const [rate, setRate] = useState(DEFAULT_EUR_RATE);
  useEffect(() => {
    const fetchRate = async () => {
      // Try yesterday first, then fall back to last available
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const dateStr = yesterday.toISOString().slice(0, 10);
      try {
        let res = await fetch(`https://api.nbp.pl/api/exchangerates/rates/a/eur/${dateStr}/?format=json`);
        if (!res.ok) {
          // Fallback: get latest available rate
          res = await fetch(`https://api.nbp.pl/api/exchangerates/rates/a/eur/last/1/?format=json`);
        }
        if (res.ok) {
          const data = await res.json();
          const mid = data?.rates?.[0]?.mid;
          if (mid && typeof mid === 'number') setRate(mid);
        }
      } catch { /* keep default */ }
    };
    fetchRate();
  }, []);
  return rate;
}

const fmtEurWithRate = (n: number, eurRate: number) => {
  const eur = Math.round(((n / eurRate) + Number.EPSILON) * 100) / 100;
  return new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(eur);
};

type CompareRow = { w: string; h: string; qty: string };

type SavedCalc = {
  id: number;
  w: string;
  h: string;
  qty: string;
  shape: string;
  shapeLabel: string;
  folia: string;
  foliaLabel: string;
  totalGross: number;
  totalNet: number;
  unitGross: number;
  unitNet: number;
  file: File | null;
  fileName: string;
};

export default function Kalkulator() {
  const { t, i18n } = useTranslation();
  const eurRate = useDynamicEurRate();
  const fmtEur = (n: number) => fmtEurWithRate(n, eurRate);
  const [searchParams, setSearchParams] = useSearchParams();
  const [w, setW] = useState(searchParams.get("w") || "");
  const [h, setH] = useState(searchParams.get("h") || "");
  const [shape, setShape] = useState(searchParams.get("shape") || "rect");
  const [folia, setFolia] = useState(searchParams.get("folia") || "biala-standard");
  const initQty = searchParams.get("qty") || "100";
  const initQtyNum = parseInt(initQty);
  const [qtyPreset, setQtyPreset] = useState<number | null>(QTY_PRESETS.includes(initQtyNum) ? initQtyNum : null);
  const [qtyCustom, setQtyCustom] = useState(initQty);
  const [error, setError] = useState("");
  const [customQtys, setCustomQtys] = useState<number[]>(() => {
    if (initQtyNum > 0 && !QTY_PRESETS.includes(initQtyNum)) return [initQtyNum];
    return [];
  });
  const [qtyInput, setQtyInput] = useState("");
  const [adjustPercent, setAdjustPercent] = useState(0);
  const [foilAdjustments, setFoilAdjustments] = useState<Record<string, number>>({});
  const [qtyPricingMap, setQtyPricingMap] = useState<Record<string, { min: number; max: number | null; adj: number }[]>>({});
  const [foilVideos, setFoilVideos] = useState<Record<string, string>>({});
  const [foilImages, setFoilImages] = useState<Record<string, string>>({});

  // Order form state
  const [orderName, setOrderName] = useState("");
  const [orderPhone, setOrderPhone] = useState("");
  const [orderNip, setOrderNip] = useState("");
  const [orderEmail, setOrderEmail] = useState("");
  const [orderStreet, setOrderStreet] = useState("");
  const [orderPostal, setOrderPostal] = useState("");
  const [orderCity, setOrderCity] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [calcFile, setCalcFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [orderSending, setOrderSending] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [savedCalcs, setSavedCalcs] = useState<SavedCalc[]>([]);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [showFormHint, setShowFormHint] = useState(false);
  const [confirmMissingFileOpen, setConfirmMissingFileOpen] = useState(false);
  const [paramsErrorOpen, setParamsErrorOpen] = useState(false);
  const [paramsErrorMessage, setParamsErrorMessage] = useState("");

  useEffect(() => {
    supabase
      .from("pricing_settings")
      .select("price_adjustment_percent")
      .limit(1)
      .single()
      .then(({ data }) => {
        if (data) setAdjustPercent(Number(data.price_adjustment_percent));
      });
    supabase
      .from("foil_pricing")
      .select("id, foil_key, adjustment_percent, preview_video_url, preview_image_url")
      .then(({ data }) => {
        if (data) {
          const map: Record<string, number> = {};
          const vids: Record<string, string> = {};
          const imgs: Record<string, string> = {};
          data.forEach((f: any) => {
            map[f.foil_key] = Number(f.adjustment_percent);
            if (f.preview_video_url) vids[f.foil_key] = f.preview_video_url;
            if (f.preview_image_url) imgs[f.foil_key] = f.preview_image_url;
          });
          setFoilAdjustments(map);
          setFoilVideos(vids);
          setFoilImages(imgs);

          // Fetch quantity pricing per foil
          const foilIdToKey: Record<string, string> = {};
          data.forEach((f: any) => { foilIdToKey[f.id] = f.foil_key; });

          supabase
            .from("foil_quantity_pricing")
            .select("foil_id, quantity_min, quantity_max, adjustment_percent")
            .order("quantity_min")
            .then(({ data: qtyData }) => {
              if (qtyData) {
                const qMap: Record<string, { min: number; max: number | null; adj: number }[]> = {};
                qtyData.forEach((q: any) => {
                  const key = foilIdToKey[q.foil_id];
                  if (key) {
                    if (!qMap[key]) qMap[key] = [];
                    qMap[key].push({ min: q.quantity_min, max: q.quantity_max, adj: Number(q.adjustment_percent) });
                  }
                });
                setQtyPricingMap(qMap);
              }
            });
        }
      });
  }, []);

  const qty = parseInt(qtyCustom) || 0;

  const getQtyTier = (foilKey: string, quantity: number): { adj: number; baseQty: number | null } => {
    if (quantity < 1) return { adj: 0, baseQty: null };
    const tiers = qtyPricingMap[foilKey];
    if (!tiers || tiers.length === 0) return { adj: 0, baseQty: null };
    const tier = tiers.find((t) => quantity >= t.min && (t.max === null || quantity <= t.max));
    return tier ? { adj: tier.adj, baseQty: tier.min } : { adj: 0, baseQty: null };
  };

  const calcLogTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastLoggedRef = useRef<string>("");

  const result = (() => {
    const wv = parseFloat(w);
    const hv = parseFloat(h);
    if (!wv || !hv || wv < 5 || hv < 5 || qty < 1 || shape === "custom") return null;
    if (wv > 380 || hv > 280) return null;
    const { adj: qtyAdj, baseQty } = getQtyTier(folia, qty);
    return calcPrice(wv, hv, qty, shape, adjustPercent, foilAdjustments[folia] || 0, qtyAdj, baseQty);
  })();

  /** Unit net price for an arbitrary quantity – used by the quantity thresholds list. */
  const unitFor = (quantity: number): number | null => {
    const wv = parseFloat(w);
    const hv = parseFloat(h);
    if (!wv || !hv || wv < 5 || hv < 5 || quantity < 1 || shape === "custom") return null;
    if (wv > 380 || hv > 280) return null;
    const { adj, baseQty } = getQtyTier(folia, quantity);
    const r = calcPrice(wv, hv, quantity, shape, adjustPercent, foilAdjustments[folia] || 0, adj, baseQty);
    return r ? r.unitNet : null;
  };

  // Keep the calculator state in the URL so a ready quote can be shared as a link
  useEffect(() => {
    const params = new URLSearchParams();
    if (w) params.set("w", w);
    if (h) params.set("h", h);
    if (qty > 0) params.set("qty", String(qty));
    if (folia) params.set("folia", folia);
    if (shape) params.set("shape", shape);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w, h, qty, folia, shape]);

  // Calculator logging disabled to save Cloud balance

  function addCustomQty(input: string) {
    const val = parseInt(input);
    if (val > 0) {
      if (!QTY_PRESETS.includes(val) && !customQtys.includes(val)) {
        setCustomQtys((prev) => [...prev, val].sort((a, b) => a - b));
      }
      setQtyCustom(String(val));
      setQtyPreset(QTY_PRESETS.includes(val) ? val : null);
      setQtyInput("");
    }
  }

  function handleCalculate() {
    // If user typed a custom qty, add it as chip automatically
    if (qtyInput.trim()) {
      addCustomQty(qtyInput);
    }
    const wv = parseFloat(w);
    const hv = parseFloat(h);
    if (!wv || !hv) { setError("Wpisz wymiary naklejki."); return; }
    if (wv < 5 || wv > 380) { setError("Szerokość: 5–380 mm"); return; }
    if (hv < 5 || hv > 280) { setError("Wysokość: 5–280 mm"); return; }
    if (shape === "custom") { setError("Kształt po obrysie – skontaktuj się z nami mailowo."); return; }
    setError("");
  }

  function handleAddToCart() {
    if (!result) { toast.error(t("calc.calcFirst")); return; }
    if (savedCalcs.length >= 10) { toast.error(t("calc.maxCalcs")); return; }
    const shapeLabels: Record<string, string> = { rect: t("calc.shapeRect"), round: t("calc.shapeRound"), custom: t("calc.shapeCustom") };
    const ksztaltLabel = shapeLabels[shape] || shape;
    const foliaLabel = t(`foils.${folia}.label`, FOLIE.find((f) => f.key === folia)?.label || folia || "Brak");
    setSavedCalcs((prev) => [...prev, {
      id: Date.now(),
      w, h, qty: qtyCustom, shape, shapeLabel: ksztaltLabel,
      folia, foliaLabel, totalGross: result.totalGross, totalNet: result.totalNet,
      unitGross: result.unitGross, unitNet: result.unitNet,
      file: calcFile, fileName: calcFile?.name || "",
    }]);
    setCalcFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast.success(`${t("calc.added")} ${savedCalcs.length + 1}/10`);
  }

  function handleRemoveCalc(id: number) {
    setSavedCalcs((prev) => prev.filter((c) => c.id !== id));
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.size > MAX_FILE_SIZE) {
      toast.error("Plik jest za duży (max 25 MB)");
      return;
    }
    setCalcFile(selected);
  };

  const submitOrder = async (force: boolean) => {
    const errors = new Set<string>();
    
    if (!orderName.trim()) errors.add("name");
    if (!orderEmail.trim()) errors.add("email");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orderEmail.trim())) errors.add("email");
    
    const hasItems = savedCalcs.length > 0 || result;
    if (!hasItems) {
      // Always require calculator parameters when no items are ready
      if (!w || parseFloat(w) < 5) errors.add("w");
      if (!h || parseFloat(h) < 5) errors.add("h");
      if (!folia) errors.add("folia");
      if (qty < 1) errors.add("qty");
    }

    if (errors.size > 0) {
      setValidationErrors(errors);
      const labels: string[] = [];
      if (errors.has("w") || errors.has("h") || errors.has("folia") || errors.has("qty")) {
        const calcFields: string[] = [];
        if (errors.has("w")) calcFields.push(t("calc.paramsErrorWidth", "szerokość"));
        if (errors.has("h")) calcFields.push(t("calc.paramsErrorHeight", "wysokość"));
        if (errors.has("folia")) calcFields.push(t("calc.paramsErrorFoil", "rodzaj folii"));
        if (errors.has("qty")) calcFields.push(t("calc.paramsErrorQty", "ilość"));
        labels.push(`${t("calc.paramsErrorStickerParams", "parametry naklejki")} (${calcFields.join(", ")})`);
      }
      if (errors.has("name")) labels.push(t("calc.paramsErrorName", "Imię / Firma"));
      if (errors.has("email")) labels.push(t("calc.paramsErrorEmail", "E-mail"));
      setParamsErrorMessage(`${t("calc.paramsErrorIntro", "Uzupełnij:")} ${labels.join(", ")}.`);
      setParamsErrorOpen(true);
      return;
    }

    setValidationErrors(new Set());

    // Build items list first to check for missing files
    const calcsToSend = savedCalcs.length > 0 ? savedCalcs : (result ? [{
      id: Date.now(), w, h, qty: qtyCustom, shape, shapeLabel: ({ rect: t("calc.shapeRect"), round: t("calc.shapeRound"), custom: t("calc.shapeCustom") } as Record<string,string>)[shape] || shape,
      folia, foliaLabel: t(`foils.${folia}.label`, FOLIE.find((f) => f.key === folia)?.label || folia || "Brak"),
      totalGross: result.totalGross, totalNet: result.totalNet,
      unitGross: result.unitGross, unitNet: result.unitNet,
      file: calcFile, fileName: calcFile?.name || "",
    }] : []);

    // Nie wysyłaj pustych zapytań – wymagaj kompletnych pozycji
    const validCalcs = calcsToSend.filter(
      (c) => parseFloat(String(c.w)) >= 5 && parseFloat(String(c.h)) >= 5 && Number(c.qty) >= 1 && !!c.folia
    );
    if (validCalcs.length === 0) {
      setValidationErrors(new Set(["w", "h", "folia", "qty"]));
      setParamsErrorMessage(t("calc.paramsErrorFullMessage", "Uzupełnij parametry naklejki (szerokość, wysokość, ilość, folia) i kliknij „Oblicz cenę” przed wysłaniem zapytania."));
      setParamsErrorOpen(true);
      return;
    }

    if (!force && validCalcs.some((c) => !c.file)) {
      setConfirmMissingFileOpen(true);
      return;
    }




    setConfirmMissingFileOpen(false);
    setOrderSending(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      // Upload files from each saved calc
      const uploadFile = async (file: File): Promise<string> => {
        const ext = file.name.split(".").pop();
        const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          });
          xhr.addEventListener("load", () => { xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Upload failed")); });
          xhr.addEventListener("error", () => reject(new Error("Upload failed")));
          xhr.open("POST", `${supabaseUrl}/storage/v1/object/order-attachments/${path}`);
          xhr.setRequestHeader("Authorization", `Bearer ${supabaseKey}`);
          xhr.setRequestHeader("apikey", supabaseKey);
          xhr.setRequestHeader("x-upsert", "false");
          xhr.send(file);
        });
        // Return the storage path; the edge function will issue a signed URL
        return path;
      };

      const items = [];
      for (const c of validCalcs) {
        let filePath = "";
        if (c.file) {
          filePath = await uploadFile(c.file);
        }
        items.push({
          ksztalt: c.shapeLabel,
          szerokosc: c.w,
          wysokosc: c.h,
          ilosc: c.qty,
          folia: c.foliaLabel,
          cenaNetto: c.totalNet,
          cenaBrutto: c.totalGross,
          cenaJedNetto: c.unitNet,
          cenaJedBrutto: c.unitGross,
          filePath,
        });
      }
      setUploadProgress(null);

      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: orderName,
          email: orderEmail,
          phone: orderPhone,
          nip: orderNip,
          street: orderStreet,
          postalCode: orderPostal,
          city: orderCity,
          items,
          uwagi: orderNotes,
          fileUrl: "",
        },
      });
      if (error) throw error;
      setOrderSent(true);
      toast.success(t("calc.orderSent"));
    } catch (err) {
      console.error(err);
      toast.error(t("calc.orderError"));
    } finally {
      setOrderSending(false);
    }
  };

  const handleOrderSubmit = () => submitOrder(false);
  const handleConfirmMissingFile = () => submitOrder(true);


  return (
    <>
      <SEOHead
        title="Kalkulator cen naklejek 3D – wycena online w 15 s"
        description="Oblicz cenę naklejek 3D zalewanych żywicą bez czekania: wybierz kształt, wymiary, nakład i folię, a wycenę otrzymasz od razu. Zamów online lub poproś o ofertę."
      />
      <Layout breadcrumbTitle="Kalkulator">
         <section className="bg-primary py-12 lg:py-20">
          <div className="max-w-6xl mx-auto px-4">
            {/* Header */}
            <div className="text-center mb-8 lg:mb-12">
              <span className="inline-block bg-accent text-accent-foreground font-extrabold text-xs tracking-widest px-4 py-1.5 rounded-full mb-4">
                {t("calc.badge")}
              </span>
              <h1 className="text-primary-foreground font-black text-3xl lg:text-5xl uppercase tracking-tight leading-tight">
                {t("calc.title")}{" "}
                <span className="text-accent">{t("calc.titleGold")}</span>
              </h1>
              <p className="text-primary-foreground/60 text-sm lg:text-base mt-3">
                {t("calc.subtitle")}
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* LEFT COLUMN – inputs */}
              <div className="flex-1 min-w-0">
                {/* Dimensions */}
                <div className="bg-navy-light border border-primary-foreground/10 rounded-2xl p-5 lg:p-7 mb-4">
                  <SectionTitle>{t("calc.dimensions")}</SectionTitle>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.width")}</label>
                      <div className="relative">
                        <input type="number" value={w} placeholder="max 380mm" onChange={(e) => { setW(e.target.value); setValidationErrors(prev => { const n = new Set(prev); n.delete("w"); return n; }); }} className={`w-full bg-primary border rounded-lg px-4 py-3.5 pr-14 text-primary-foreground text-base placeholder:text-primary-foreground/30 outline-none focus:border-accent transition-colors ${validationErrors.has("w") ? "border-destructive ring-1 ring-destructive/50" : "border-primary-foreground/15"}`} />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-primary-foreground/50 text-base">mm</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.height")}</label>
                      <div className="relative">
                        <input type="number" value={h} placeholder="max 280mm" onChange={(e) => { setH(e.target.value); setValidationErrors(prev => { const n = new Set(prev); n.delete("h"); return n; }); }} className={`w-full bg-primary border rounded-lg px-4 py-3.5 pr-14 text-primary-foreground text-base placeholder:text-primary-foreground/30 outline-none focus:border-accent transition-colors ${validationErrors.has("h") ? "border-destructive ring-1 ring-destructive/50" : "border-primary-foreground/15"}`} />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-primary-foreground/50 text-base">mm</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live schematic preview */}
                <StickerPreview
                  width={parseFloat(w) || 0}
                  height={parseFloat(h) || 0}
                  shape={shape}
                  foilVideoUrl={foilVideos[folia]}
                  foilImageUrl={foilImages[folia]}
                  foilLabel={t(`foils.${folia}.label`, FOLIE.find((x) => x.key === folia)?.label || folia)}
                  background={(() => {
                    const f = FOLIE.find((x) => x.key === folia);
                    return f ? (f.gradient || f.color) : undefined;
                  })()}
                  borderColor={FOLIE.find((x) => x.key === folia)?.border}
                />

                {/* Shape */}
                <div className="bg-navy-light border border-primary-foreground/10 rounded-2xl p-5 lg:p-7 mb-4">
                  <SectionTitle>{t("calc.shape")}</SectionTitle>
                  <div className="grid grid-cols-3 gap-3">
                    {SHAPE_KEYS.map((shKey) => {
                      const labelMap: Record<string, string> = { rect: t("calc.shapeRect"), round: t("calc.shapeRound"), custom: t("calc.shapeCustom") };
                      const descMap: Record<string, string> = { rect: t("calc.shapeRectDesc"), round: t("calc.shapeRoundDesc"), custom: t("calc.shapeCustomDesc") };
                      return (
                      <button
                        key={shKey}
                        onClick={() => setShape(shKey)}
                        className={`rounded-xl py-5 px-3 text-center border-2 transition-all ${shape === shKey ? "bg-accent/15 border-accent shadow-gold" : "bg-primary border-primary-foreground/10 hover:border-primary-foreground/25"}`}
                      >
                        <div className="flex justify-center mb-3">
                          <ShapeIcon shapeKey={shKey} active={shape === shKey} />
                        </div>
                        <div className={`text-sm font-extrabold tracking-wide ${shape === shKey ? "text-accent" : "text-primary-foreground"}`}>{labelMap[shKey]}</div>
                        <div className="text-primary-foreground/40 text-xs mt-1">{descMap[shKey]}</div>
                      </button>
                      );
                    })}
                  </div>
                  {shape === "custom" && (
                    <div className="bg-accent/10 border border-accent/30 rounded-xl p-4 mt-3 text-sm">
                      <p className="text-accent font-bold mb-1">⚠ {t("calc.customShapeNote")}</p>
                      <p className="text-primary-foreground/50 text-xs">{t("calc.customShapeDesc")}</p>
                    </div>
                  )}
                </div>

                {/* Foil */}
                <div className="bg-navy-light border border-primary-foreground/10 rounded-2xl p-5 lg:p-7 mb-4">
                  <SectionTitle>{t("calc.foilType")}</SectionTitle>

                  <div className="bg-accent/10 border border-accent/30 rounded-lg px-3 py-2 mb-3 flex items-center gap-2 animate-pulse">
                    <Film className="w-5 h-5 shrink-0 text-accent" />
                    <p className="text-accent text-sm font-semibold">
                      {t("calc.foilHint", "Kliknij w folię, aby zobaczyć jak wygląda")}
                    </p>
                    <svg className="w-4 h-4 text-accent ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>

                  {/* Media preview of selected foil */}
                  {(foilVideos[folia] || foilImages[folia]) && (
                    <div className="mb-4 rounded-xl overflow-hidden border border-accent/30 bg-black/20">
                      {foilVideos[folia] ? (
                        <video
                          key={foilVideos[folia]}
                          src={foilVideos[folia]}
                          className="w-full max-h-44 object-cover"
                          autoPlay
                          loop
                          muted
                          playsInline
                        />
                      ) : (
                        <img
                          key={foilImages[folia]}
                          src={foilImages[folia]}
                          alt={folia}
                          className="w-full max-h-44 object-cover"
                        />
                      )}
                    </div>
                  )}

                  <div className="space-y-2 max-h-60 lg:max-h-80 overflow-y-auto">
                    {FOLIE.map((f) => (
                      <button
                        key={f.key}
                        onClick={() => setFolia(f.key)}
                        className={`flex items-center gap-3 w-full text-left rounded-lg px-3 py-3 border transition-colors ${folia === f.key ? "bg-accent/15 border-accent" : "bg-primary border-primary-foreground/10"}`}
                      >
                        <span className="relative w-6 h-6 shrink-0">
                          <span
                            className="w-6 h-6 rounded border block"
                            style={{
                              background: ("gradient" in f && f.gradient) ? f.gradient : f.color,
                              borderColor: f.border,
                            }}
                          />
                          {folia === f.key && (
                            <CheckCircle className="w-4 h-4 text-green-400 absolute -top-1.5 -right-1.5 drop-shadow" />
                          )}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${folia === f.key ? "text-accent" : "text-primary-foreground"}`}>{t(`foils.${f.key}.label`, f.label)}</span>
                            {foilVideos[f.key] && (
                              <Film className="w-3.5 h-3.5 text-accent/50 shrink-0" />
                            )}
                            {!foilVideos[f.key] && foilImages[f.key] && (
                              <ImageIcon className="w-3.5 h-3.5 text-accent/50 shrink-0" />
                            )}
                          </div>
                          {f.desc && (
                            <p className="text-primary-foreground/40 text-xs mt-0.5 leading-snug">{t(`foils.${f.key}.desc`, f.desc)}</p>
                          )}
                          {"hint" in f && f.hint && (
                            <p className="text-accent/70 text-xs mt-0.5 italic">{t(`foils.${f.key}.hint`, f.hint as string)}</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>


                {error && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-destructive text-sm font-semibold mb-3">
                    ⚠ {error}
                  </div>
                )}

                {/* Cena liczy się automatycznie - przycisk usunięty */}
              </div>

              {/* RIGHT COLUMN – result + order */}
              <div className="w-full lg:w-[420px] shrink-0">
                <div className="lg:sticky lg:top-24 space-y-4">

                {/* Quantity + File attachment */}
                <div className="bg-navy-light border border-primary-foreground/10 rounded-2xl p-5 lg:p-7">
                  <SectionTitle>{t("calc.quantity")}</SectionTitle>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {QTY_PRESETS.map((q) => (
                      <button
                        key={q}
                        onClick={() => { setQtyPreset(q); setQtyCustom(String(q)); }}
                        className={`rounded-lg px-4 py-2.5 text-sm font-bold border transition-colors ${String(q) === qtyCustom ? "bg-accent text-accent-foreground border-accent" : "bg-primary text-primary-foreground/60 border-primary-foreground/15"}`}
                      >
                        {q.toLocaleString("pl-PL")} {t("calc.pcs")}
                      </button>
                    ))}
                    {customQtys.map((q) => (
                      <button
                        key={`c-${q}`}
                        onClick={() => { setQtyCustom(String(q)); setQtyPreset(null); }}
                        className={`rounded-lg px-4 py-2.5 text-sm font-bold border transition-colors relative group ${String(q) === qtyCustom ? "bg-accent text-accent-foreground border-accent" : "bg-primary text-primary-foreground/60 border-primary-foreground/15"}`}
                      >
                        {q.toLocaleString("pl-PL")} {t("calc.pcs")}
                        <span
                          onClick={(e) => { e.stopPropagation(); setCustomQtys((prev) => prev.filter((v) => v !== q)); if (String(q) === qtyCustom) { setQtyCustom("100"); setQtyPreset(100); } }}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-destructive text-destructive-foreground rounded-full text-[9px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        >✕</span>
                      </button>
                    ))}
                  </div>
                  <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.addCustomQty")}</label>
                  <div className="flex gap-2 mb-5">
                    <div className="relative flex-1">
                      <input
                        type="number"
                        min="1"
                        value={qtyInput}
                        onChange={(e) => {
                          setQtyInput(e.target.value);
                          const val = parseInt(e.target.value);
                          if (val > 0) {
                            setQtyCustom(String(val));
                            setQtyPreset(null);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addCustomQty(qtyInput);
                          }
                        }}
                        className="w-full bg-primary border border-primary-foreground/15 rounded-lg px-4 py-3.5 pr-14 text-primary-foreground text-base outline-none focus:border-accent"
                        placeholder="np. 250"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-primary-foreground/50 text-base">{t("calc.pcs")}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => addCustomQty(qtyInput)}
                      className="bg-accent text-accent-foreground font-bold px-5 rounded-lg text-base hover:opacity-90 transition-opacity"
                    >
                      {t("calc.add")}
                    </button>
                  </div>

                  <QuantityTiers
                    currentQty={qty}
                    unitFor={unitFor}
                    onSelect={(q) => {
                      setQtyCustom(String(q));
                      setQtyPreset(QTY_PRESETS.includes(q) ? q : null);
                      if (!QTY_PRESETS.includes(q) && !customQtys.includes(q)) {
                        setCustomQtys((prev) => [...prev, q].sort((a, b) => a - b));
                      }
                    }}
                  />

                  {result && (
                    <button
                      onClick={handleAddToCart}
                      disabled={savedCalcs.length >= 10}
                      className={`w-full mb-4 border-2 border-accent text-accent font-bold text-sm uppercase tracking-wider rounded-xl py-3 hover:bg-accent/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${savedCalcs.length === 0 ? 'animate-[strongPulse_1.5s_ease-in-out_infinite] shadow-[0_0_25px_rgba(212,175,55,0.6)]' : ''}`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {t("calc.addToOrder")}
                    </button>
                  )}

                  <div className="border-t border-primary-foreground/10 pt-4">
                    <SectionTitle>{t("calc.attachGraphic")}</SectionTitle>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} accept=".pdf,.png,.jpg,.jpeg,.svg,.ai,.eps,.cdr,.tiff,.webp" />
                    {calcFile ? (
                      <div className="space-y-2">
                        {calcFile.type.startsWith("image/") && (
                          <img
                            src={URL.createObjectURL(calcFile)}
                            alt="Podgląd"
                            className="w-full max-h-32 object-contain rounded-lg border border-primary-foreground/10 bg-primary-foreground/5"
                          />
                        )}
                        <div className="flex items-center gap-3 p-3.5 rounded-lg border border-accent/30 bg-accent/10">
                          <Paperclip className="w-5 h-5 text-accent shrink-0" />
                          <span className="text-base text-primary-foreground truncate flex-1">{calcFile.name}</span>
                          <button type="button" onClick={() => { setCalcFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                            className="text-primary-foreground/60 hover:text-destructive transition-colors"><X className="w-5 h-5" /></button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileInputRef.current?.click()}
                        className="w-full p-3.5 rounded-lg border border-dashed border-primary-foreground/15 hover:border-accent/50 text-primary-foreground/40 hover:text-primary-foreground/60 transition-colors text-base">
                        <Paperclip className="w-5 h-5 inline mr-2" />{t("calc.attachClick")}
                      </button>
                    )}
                    <p className="text-primary-foreground/30 text-xs mt-2">
                      {t("calc.largeFiles")} <a href="mailto:biuro@projekt-art.pl" className="text-accent hover:underline">biuro@projekt-art.pl</a> / <a href="https://wetransfer.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">WeTransfer</a>.
                    </p>
                  </div>
                </div>

                {result && (
                  <div className="bg-navy-light border-2 border-accent rounded-2xl p-5 lg:p-7">
                    <div className="text-center bg-accent rounded-xl p-5 mb-4">
                      <p className="text-accent-foreground text-xs font-extrabold tracking-widest uppercase mb-1">{t("calc.totalNet")}</p>
                      <p className="text-accent-foreground text-5xl font-black">{fmt(result.totalNet)} zł</p>
                      <p className="text-accent-foreground/80 text-sm font-bold mt-1">~{fmtEur(result.totalNet)} €</p>
                      <p className="text-accent-foreground/80 text-base font-bold mt-1">
                        {t("calc.gross")}: {fmt(result.totalGross)} zł
                        <span className="text-accent-foreground/60 text-xs ml-2">~{fmtEur(result.totalGross)} €</span>
                      </p>
                      <p className="text-accent-foreground/60 text-xs mt-0.5">{t("calc.vatIncl")} {VAT}%: {fmt(result.vatAmount)} zł</p>
                      <p className="text-accent-foreground/70 text-xs mt-1.5 font-medium leading-snug">{t("calc.euVatZero")}</p>
                      <p className="text-accent-foreground/50 text-[10px] mt-2 leading-snug">🕐 {t("calc.deliveryNote")}</p>
                      <div className="mt-3 pt-2 border-t-2 border-accent-foreground/20">
                        <p className="text-accent-foreground text-sm font-bold leading-snug flex items-center justify-center gap-2">
                          <span className="text-lg">📦</span>
                          {t("calc.minOrderInfo", "Minimum logistyczne, jakie realizujemy, zaczyna się od kwoty 100 zł netto")}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {[
                        { l: t("calc.unitNet"), v: `${fmt(result.unitNet)} zł`, eur: null },
                        { l: t("calc.unitGross"), v: `${fmt(result.unitGross)} zł`, eur: `~${fmtEur(result.unitGross)} €` },
                      ].map((r) => (
                        <div key={r.l} className="bg-primary rounded-lg p-3 text-center">
                          <p className="text-primary-foreground/50 text-xs tracking-wider uppercase mb-1">{r.l}</p>
                          <p className="text-primary-foreground text-base font-extrabold">{r.v}</p>
                          {r.eur && <p className="text-primary-foreground/40 text-xs mt-0.5">{r.eur}</p>}
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-dashed border-primary-foreground/15 pt-3">
                      <p className="text-primary-foreground/50 text-xs tracking-wider uppercase mb-2">{t("calc.details")}</p>
                      {[
                        { l: t("calc.dimLabel"), v: `${w} × ${h} mm` },
                        { l: t("calc.qtyLabel"), v: `${qty} ${t("calc.pcs")}` },
                        ...(folia ? [{ l: t("calc.foilLabel"), v: FOLIE.find((f) => f.key === folia)?.label || "" }] : []),
                      ].map((r) => (
                        <div key={r.l} className="flex justify-between text-sm mb-1.5">
                          <span className="text-primary-foreground/50">{r.l}</span>
                          <span className="text-primary-foreground font-medium">{r.v}</span>
                        </div>
                      ))}
                    </div>



                    {/* Add to cart button */}
                    <button
                      onClick={handleAddToCart}
                      disabled={savedCalcs.length >= 10}
                      className={`w-full mt-3 border-2 border-accent text-accent font-bold text-sm uppercase tracking-wider rounded-xl py-3 hover:bg-accent/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${savedCalcs.length === 0 ? 'animate-[strongPulse_1.5s_ease-in-out_infinite] shadow-[0_0_25px_rgba(212,175,55,0.6)]' : ''}`}
                    >
                      <Plus className="w-4 h-4" />
                      {savedCalcs.length >= 10 ? t("calc.maxItems") : `${t("calc.addToOrder")} (${savedCalcs.length}/10)`}
                    </button>

                    {/* Copy config link button */}
                    <button
                      onClick={() => {
                        const params = new URLSearchParams();
                        if (w) params.set("w", w);
                        if (h) params.set("h", h);
                        if (shape && shape !== "rect") params.set("shape", shape);
                        if (folia) params.set("folia", folia);
                        if (qtyCustom && qtyCustom !== "100") params.set("qty", qtyCustom);
                        const url = `${window.location.origin}/kalkulator${params.toString() ? "?" + params.toString() : ""}`;
                        navigator.clipboard.writeText(url).then(() => {
                          toast.success(t("calc.configCopied"));
                        });
                      }}
                      className="w-full mt-2 border border-primary-foreground/20 text-primary-foreground/70 font-medium text-xs uppercase tracking-wider rounded-xl py-2.5 hover:bg-primary-foreground/5 transition-colors flex items-center justify-center gap-2"
                    >
                      <Link2 className="w-3.5 h-3.5" />
                      {t("calc.copyConfig")}
                    </button>

                    {/* PDF download button */}
                    <button
                      disabled={pdfGenerating}
                      onClick={async () => {
                        if (!result || pdfGenerating) return;
                        setPdfGenerating(true);
                        try {
                        const { default: jsPDF } = await import('jspdf');
                        const { default: html2canvas } = await import('html2canvas');
                        const lang = i18n.language?.slice(0, 2) || 'pl';
                        const dateLocale = lang === 'no' ? 'nb-NO' : lang === 'pl' ? 'pl-PL' : lang === 'de' ? 'de-DE' : lang === 'fr' ? 'fr-FR' : 'en-GB';
                        const today = new Date().toLocaleDateString(dateLocale);

                        // PDF translation labels
                        const p = {
                          badge: t("calc.badge"),
                          title: t("calc.pdfTitle"),
                          position: t("calc.pdfPosition"),
                          of: t("calc.pdfOf"),
                          gross: t("calc.pdfGross"),
                          dimensions: t("calc.pdfDimensions"),
                          shape: t("calc.pdfShape"),
                          quantity: t("calc.pdfQuantity"),
                          unitNet: t("calc.pdfUnitNet"),
                          foil: t("calc.pdfFoil"),
                          netValue: t("calc.pdfNetValue"),
                          vat: t("calc.pdfVat"),
                          sumNet: t("calc.pdfSumNet"),
                          totalGross: t("calc.pdfTotalGross"),
                          date: t("calc.pdfDate"),
                          disclaimer: t("calc.pdfDisclaimer"),
                          positions: t("calc.pdfPositions"),
                          pcs: t("calc.pcs"),
                          shipping: t("calc.pdfShipping", "Wysyłka kurier (netto)"),
                        };

                        // Build items list: saved calcs + current
                        const currentFoliaLabel = t(`foils.${folia}.label`, FOLIE.find((f) => f.key === folia)?.label || folia);
                        const currentShapeLabel = ({ rect: t("calc.shapeRect"), round: t("calc.shapeRound"), custom: t("calc.shapeCustom") } as Record<string,string>)[shape] || shape;
                        const currentItem = {
                          w, h, qty: String(qty), shapeLabel: currentShapeLabel,
                          foliaLabel: currentFoliaLabel, foliaKey: folia, totalGross: result.totalGross,
                          totalNet: result.totalNet, unitGross: result.unitGross,
                          unitNet: result.unitNet, vatAmount: result.vatAmount,
                        };

                        const allItems = savedCalcs.length > 0
                          ? savedCalcs.map(sc => {
                              const vatAmt = Math.round(sc.totalNet * 23) / 100;
                              return { w: sc.w, h: sc.h, qty: sc.qty, shapeLabel: sc.shapeLabel, foliaLabel: sc.foliaLabel, foliaKey: sc.folia, totalGross: sc.totalGross, totalNet: sc.totalNet, unitGross: sc.unitGross, unitNet: sc.unitNet, vatAmount: vatAmt };
                            })
                          : [currentItem];

                        if (savedCalcs.length > 0) {
                          const isDuplicate = savedCalcs.some(sc => sc.w === w && sc.h === h && sc.qty === String(qty) && sc.folia === folia && sc.shape === shape);
                          if (!isDuplicate) {
                            allItems.push(currentItem);
                          }
                        }

                        // Courier shipping always added: 18 zł netto + 23% VAT
                        const shippingNet = 18;
                        const shippingVat = Math.round(shippingNet * 23) / 100;
                        const shippingGross = shippingNet + shippingVat;

                        const itemsNet = allItems.reduce((s, i) => s + i.totalNet, 0);
                        const itemsVat = allItems.reduce((s, i) => s + i.vatAmount, 0);
                        const grandTotalNet = itemsNet + shippingNet;
                        const grandTotalVat = itemsVat + shippingVat;
                        const grandTotalGross = allItems.reduce((s, i) => s + i.totalGross, 0) + shippingGross;

                        // Load logo
                        let logoSrc = '';
                        try {
                          const logoModule = await import('@/assets/nowe-logo-naklejki-3d.png.asset.json');
                          const resp = await fetch(logoModule.default.url);
                          const blob = await resp.blob();
                          logoSrc = await new Promise<string>((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result as string);
                            reader.readAsDataURL(blob);
                          });
                        } catch { /* fallback */ }

                        // Load foil preview images (image or video screenshot)
                        const foilPreviewDataUrls: Record<string, string> = {};
                        const uniqueFoilKeys = [...new Set(allItems.map(i => i.foliaKey))];
                        await Promise.all(uniqueFoilKeys.map(async (key) => {
                          try {
                            const imgUrl = foilImages[key];
                            const vidUrl = foilVideos[key];
                            if (imgUrl) {
                              const resp = await fetch(imgUrl);
                              const blob = await resp.blob();
                              foilPreviewDataUrls[key] = await new Promise<string>((resolve) => {
                                const r = new FileReader();
                                r.onloadend = () => resolve(r.result as string);
                                r.readAsDataURL(blob);
                              });
                            } else if (vidUrl) {
                              // Capture frame from video
                              const video = document.createElement('video');
                              video.crossOrigin = 'anonymous';
                              video.muted = true;
                              video.src = vidUrl;
                              await new Promise<void>((resolve, reject) => {
                                video.onloadeddata = () => { video.currentTime = 0.5; };
                                video.onseeked = () => resolve();
                                video.onerror = () => reject();
                                video.load();
                                setTimeout(() => resolve(), 5000);
                              });
                              const cvs = document.createElement('canvas');
                              cvs.width = video.videoWidth || 320;
                              cvs.height = video.videoHeight || 180;
                              const ctx = cvs.getContext('2d');
                              if (ctx) {
                                ctx.drawImage(video, 0, 0, cvs.width, cvs.height);
                                foilPreviewDataUrls[key] = cvs.toDataURL('image/jpeg', 0.85);
                              }
                            }
                          } catch { /* skip preview */ }
                        }));
                        const watermarks = logoSrc ? `
                          <img src="${logoSrc}" style="position:absolute;top:6%;left:4%;width:200px;opacity:0.05;transform:rotate(-25deg);" />
                          <img src="${logoSrc}" style="position:absolute;top:32%;right:3%;width:200px;opacity:0.05;transform:rotate(-25deg);" />
                          <img src="${logoSrc}" style="position:absolute;top:58%;left:12%;width:200px;opacity:0.05;transform:rotate(-25deg);" />
                          <img src="${logoSrc}" style="position:absolute;top:78%;right:10%;width:200px;opacity:0.05;transform:rotate(-25deg);" />
                        ` : `
                          <div style="position:absolute;top:8%;left:5%;font-size:36px;font-weight:900;opacity:0.04;transform:rotate(-25deg);">Wypukle.pl</div>
                          <div style="position:absolute;top:35%;right:3%;font-size:36px;font-weight:900;opacity:0.04;transform:rotate(-25deg);">Wypukle.pl</div>
                          <div style="position:absolute;top:60%;left:15%;font-size:36px;font-weight:900;opacity:0.04;transform:rotate(-25deg);">Wypukle.pl</div>
                          <div style="position:absolute;top:82%;right:10%;font-size:36px;font-weight:900;opacity:0.04;transform:rotate(-25deg);">Wypukle.pl</div>
                        `;

                        // Build items HTML with translated labels + EUR
                        const itemsHtml = allItems.map((item, idx) => {
                          const previewSrc = foilPreviewDataUrls[item.foliaKey] || '';
                          const previewHtml = previewSrc ? `
                            <div style="margin:10px 0 14px;text-align:center;">
                              <img src="${previewSrc}" style="max-width:100%;max-height:120px;border-radius:6px;border:1px solid #e8e4d8;object-fit:cover;" />
                              <div style="font-size:9px;color:#aaa;margin-top:4px;">Podgląd folii</div>
                            </div>
                          ` : '';
                          return `
                          <div class="pdf-block" style="margin-bottom:24px;${idx > 0 ? 'padding-top:20px;border-top:2px solid #e8e4d8;' : ''}">
                            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;">
                              <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#d4a017;">
                                ${p.position} ${idx + 1}${allItems.length > 1 ? ` ${p.of} ${allItems.length}` : ''}
                              </div>
                            </div>
                            <!-- Details grid -->
                            <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:12px;">
                              <tr style="border-bottom:1px solid #f0f0f0;">
                                <td style="padding:7px 0;color:#888;width:25%;">${p.dimensions}</td>
                                <td style="padding:7px 0;font-weight:600;width:25%;">${item.w} × ${item.h} mm</td>
                                <td style="padding:7px 0;color:#888;width:25%;">${p.shape}</td>
                                <td style="padding:7px 0;font-weight:600;text-align:right;">${item.shapeLabel}</td>
                              </tr>
                              <tr style="border-bottom:1px solid #f0f0f0;">
                                <td style="padding:7px 0;color:#888;">${p.quantity}</td>
                                <td style="padding:7px 0;font-weight:600;">${item.qty} ${p.pcs}</td>
                                <td style="padding:7px 0;color:#888;">${p.unitNet}</td>
                                <td style="padding:7px 0;font-weight:600;text-align:right;">${fmt(item.unitNet)} zł</td>
                              </tr>
                              <tr>
                                <td style="padding:7px 0;color:#888;vertical-align:top;">${p.foil}</td>
                                <td colspan="3" style="padding:7px 0;font-weight:600;font-size:11px;word-break:break-word;">${item.foliaLabel}</td>
                              </tr>
                            </table>
                            ${previewHtml}
                            <!-- Price summary box -->
                            <div style="background:#faf8f3;border:1px solid #ede9dd;border-radius:6px;padding:10px 14px;">
                              <table style="width:100%;border-collapse:collapse;font-size:12px;">
                                <tr>
                                  <td style="padding:4px 0;color:#888;">NETTO</td>
                                  <td style="padding:4px 0;font-weight:600;text-align:right;">${fmt(item.totalNet)} zł</td>
                                </tr>
                                <tr>
                                  <td style="padding:4px 0;color:#888;">${p.vat} (23%)</td>
                                  <td style="padding:4px 0;font-weight:600;text-align:right;">${fmt(item.vatAmount)} zł</td>
                                </tr>
                                <tr style="border-top:1.5px solid #d4a017;">
                                  <td style="padding:8px 0 2px;font-weight:700;color:#1a1a2e;font-size:13px;">BRUTTO</td>
                                  <td style="padding:8px 0 2px;font-weight:700;color:#d4a017;text-align:right;font-size:14px;">${fmt(item.totalGross)} zł <span style="font-size:10px;color:#888;font-weight:400;">~${fmtEur(item.totalGross)} €</span></td>
                                </tr>
                              </table>
                            </div>
                          </div>
                        `}).join('');

                        const shippingRow = `
                              <tr>
                                <td style="color:#aab;padding:5px 0;">${p.shipping}</td>
                                <td style="font-weight:600;text-align:right;color:#ccc;">${fmt(shippingNet)} zł</td>
                              </tr>`;
                        const summaryHtml = `
                          <div class="pdf-block" style="background:#1a1a2e;padding:18px 20px;margin-top:${allItems.length > 1 ? 14 : 10}px;border-radius:8px;color:#fff;">
                            <table style="width:100%;font-size:13px;">
                              <tr>
                                <td style="color:#aab;padding:5px 0;">${p.netValue}</td>
                                <td style="font-weight:600;text-align:right;color:#ccc;">${fmt(itemsNet)} zł</td>
                              </tr>
                              ${shippingRow}
                              <tr>
                                <td style="color:#aab;padding:5px 0;">NETTO</td>
                                <td style="font-weight:600;text-align:right;color:#ccc;">${fmt(grandTotalNet)} zł</td>
                              </tr>
                              <tr>
                                <td style="color:#aab;padding:5px 0;">${p.vat} (23%)</td>
                                <td style="font-weight:600;text-align:right;color:#ccc;">${fmt(grandTotalVat)} zł</td>
                              </tr>
                              <tr style="border-top:2px solid #d4a017;">
                                <td style="font-size:16px;font-weight:800;color:#fff;padding:12px 0 2px;">BRUTTO</td>
                                <td style="font-size:20px;font-weight:800;color:#d4a017;text-align:right;padding:12px 0 2px;">${fmt(grandTotalGross)} zł <span style="font-size:12px;color:#aab;font-weight:400;">~${fmtEur(grandTotalGross)} €</span></td>
                              </tr>
                            </table>
                          </div>
                        `;

                        const container = document.createElement('div');
                        container.style.cssText = 'position:fixed;left:-9999px;top:0;width:794px;background:#fff;font-family:Segoe UI,Arial,sans-serif;color:#1a1a2e;';
                        container.innerHTML = `
                          <div style="padding:50px 55px;position:relative;">
                            <div style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:0;">${watermarks}</div>
                            <div style="position:relative;z-index:1;">
                              <div class="pdf-block" style="text-align:center;margin-bottom:30px;padding-bottom:16px;border-bottom:3px solid #d4a017;">
                                ${logoSrc ? `<img src="${logoSrc}" style="height:50px;margin-bottom:10px;" />` : ''}
                                <div style="display:inline-block;background:#d4a017;color:#fff;font-size:10px;font-weight:700;letter-spacing:2px;padding:4px 16px;border-radius:20px;margin-bottom:8px;">${p.badge}</div>
                                <h1 style="font-size:26px;color:#1a1a2e;margin:8px 0 4px;">${p.title}${allItems.length > 1 ? ` (${allItems.length} ${p.positions})` : ''}</h1>
                                <p style="color:#888;font-size:12px;">${p.date}: ${today}</p>
                              </div>
                              ${itemsHtml}
                              ${summaryHtml}
                              <div class="pdf-block" style="margin-top:40px;padding-top:15px;border-top:1px solid #ddd;text-align:center;color:#aaa;font-size:10px;">
                                <p>naklejki-3d.com.pl · biuro@projekt-art.pl</p>
                                <p style="margin-top:4px;">${p.disclaimer}</p>
                              </div>
                            </div>
                          </div>
                        `;

                        document.body.appendChild(container);

                        try {
                          const canvas = await html2canvas(container, { scale: 3, useCORS: true, backgroundColor: '#ffffff' });

                          const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
                          const pageW = doc.internal.pageSize.getWidth();
                          const pageH = doc.internal.pageSize.getHeight();
                          const imgW = pageW;
                          const imgH = (canvas.height * imgW) / canvas.width;

                          if (imgH <= pageH) {
                            doc.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, imgW, imgH);
                          } else {
                            // Map DOM block boundaries to canvas pixels so we never cut a block in half
                            const containerTop = container.getBoundingClientRect().top;
                            const pxToCanvas = canvas.height / container.offsetHeight;
                            const blocks = Array.from(container.querySelectorAll<HTMLElement>('.pdf-block'));
                            const boundaries = blocks
                              .map((b) => (b.getBoundingClientRect().bottom - containerTop) * pxToCanvas)
                              .filter((y) => y > 0 && y < canvas.height)
                              .sort((a, b) => a - b);

                            const canvasPageH = (pageH / imgW) * canvas.width;
                            let srcY = 0;
                            let page = 0;
                            while (srcY < canvas.height - 1) {
                              // Largest block boundary that still fits on this page
                              const maxY = srcY + canvasPageH;
                              let cutY = boundaries.filter((y) => y > srcY + 1 && y <= maxY).pop() ?? maxY;
                              cutY = Math.min(cutY, canvas.height);
                              const srcSliceH = cutY - srcY;
                              if (srcSliceH <= 0) break;

                              if (page > 0) doc.addPage();
                              const sliceCanvas = document.createElement('canvas');
                              sliceCanvas.width = canvas.width;
                              sliceCanvas.height = srcSliceH;
                              const ctx = sliceCanvas.getContext('2d')!;
                              ctx.fillStyle = '#ffffff';
                              ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
                              ctx.drawImage(canvas, 0, srcY, canvas.width, srcSliceH, 0, 0, canvas.width, srcSliceH);
                              const renderH = (srcSliceH * imgW) / canvas.width;
                              doc.addImage(sliceCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, imgW, renderH);
                              srcY = cutY;
                              page++;
                            }
                          }


                          const langPrefix = lang !== 'pl' ? `${lang}-` : '';
                          const fileName = allItems.length > 1
                            ? `${langPrefix}oferta-naklejki3d-${allItems.length}poz.pdf`
                            : `${langPrefix}oferta-naklejki3d-${w}x${h}-${qty}.pdf`;
                          doc.save(fileName);
                        } finally {
                          document.body.removeChild(container);
                        }
                        } finally {
                          setPdfGenerating(false);
                        }
                      }}
                      className={`w-full mt-2 border border-primary-foreground/20 text-primary-foreground/70 font-medium text-xs uppercase tracking-wider rounded-xl py-2.5 hover:bg-primary-foreground/5 transition-colors flex items-center justify-center gap-2 ${pdfGenerating ? 'opacity-60 cursor-wait' : ''}`}
                    >
                      {pdfGenerating ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Generowanie PDF...
                        </>
                      ) : (
                        <>
                          <Download className="w-3.5 h-3.5" />
                          {t("calc.downloadPdf")}{savedCalcs.length > 0 ? ` (${savedCalcs.length + 1} poz.)` : ''}
                        </>
                      )}
                    </button>

                    {/* Scroll to order form button */}
                    <button
                      onClick={() => {
                        const el = document.getElementById('order-form-section');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          setShowFormHint(true);
                          setTimeout(() => setShowFormHint(false), 12000);
                        }
                      }}
                      className="w-full mt-2 gradient-gold text-accent-foreground font-bold text-sm uppercase tracking-wider rounded-xl py-3 shadow-gold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      {shape === "custom" ? t("calc.sendInquiry") : t("calc.sendOrder")}
                    </button>
                  </div>
                )}

                {/* No result yet - prompt */}
                {!result && (
                  <div className="bg-navy-light border border-primary-foreground/10 rounded-2xl p-5 lg:p-7 text-center">
                    <p className="text-primary-foreground/40 text-sm">{t("calc.fillDimensions")}</p>
                  </div>
                )}
                </div>
              </div>
            </div>

            {/* Order form – full width below columns */}
            <div id="order-form-section" className="bg-navy-light border border-primary-foreground/10 rounded-2xl p-5 lg:p-7 mt-6">
              <div className="flex items-center justify-between mb-1">
                <SectionTitle>{shape === "custom" ? t("calc.sendInquiry") : t("calc.sendOrder")}</SectionTitle>
                {savedCalcs.length > 0 && (
                  <span className="flex items-center gap-1.5 bg-accent/15 text-accent text-xs font-bold px-3 py-1 rounded-full shrink-0 mb-4">
                    <ShoppingCart className="w-3.5 h-3.5" />
                    {savedCalcs.length}
                  </span>
                )}
              </div>

              {/* Saved calculations list */}
              {savedCalcs.length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-primary-foreground/50 text-xs font-bold tracking-widest uppercase">{t("calc.orderItems")}</p>
                  {savedCalcs.map((calc, i) => (
                    <div key={calc.id} className="flex items-center gap-3 bg-primary border border-primary-foreground/10 rounded-lg p-3">
                      <span className="text-accent font-black text-sm w-6 text-center">{i + 1}</span>
                      {calc.file && calc.file.type.startsWith("image/") && (
                        <img
                          src={URL.createObjectURL(calc.file)}
                          alt="Podgląd"
                          className="w-10 h-10 rounded object-cover border border-primary-foreground/10 shrink-0"
                        />
                      )}
                      {calc.file && !calc.file.type.startsWith("image/") && (
                        <div className="w-10 h-10 rounded bg-accent/10 border border-primary-foreground/10 flex items-center justify-center shrink-0">
                          <Paperclip className="w-4 h-4 text-accent/60" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-primary-foreground text-sm font-medium truncate">
                          {calc.w}×{calc.h}mm · {calc.qty} {t("calc.pcs")} · {calc.shapeLabel}
                        </p>
                        <p className="text-primary-foreground/40 text-xs truncate">{calc.foliaLabel}</p>
                        {calc.fileName && (
                          <p className="text-accent/60 text-xs truncate flex items-center gap-1 mt-0.5">
                            <Paperclip className="w-3 h-3" />{calc.fileName}
                          </p>
                        )}
                      </div>
                      <span className="text-right whitespace-nowrap">
                        <span className="block text-accent font-bold text-sm">{fmt(calc.totalGross)} zł <span className="text-primary-foreground/40 text-xs">~{fmtEur(calc.totalGross)} €</span></span>
                        <span className="block text-primary-foreground/50 text-xs">{fmt(calc.totalNet)} zł netto</span>
                      </span>

                      <button onClick={() => handleRemoveCalc(calc.id)} className="text-primary-foreground/30 hover:text-destructive transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <div className="pt-2 border-t border-dashed border-primary-foreground/15 space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-primary-foreground/50 text-xs font-bold tracking-widest uppercase">{t("calc.totalNet")}</span>
                      <span className="text-primary-foreground/70 font-bold text-sm">{fmt(savedCalcs.reduce((sum, c) => sum + c.totalNet, 0))} zł <span className="text-primary-foreground/40 text-xs font-normal">~{fmtEur(savedCalcs.reduce((sum, c) => sum + c.totalNet, 0))} €</span></span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-primary-foreground/50 text-xs font-bold tracking-widest uppercase">{t("calc.totalGross")}</span>
                      <span className="text-accent font-black text-sm">{fmt(savedCalcs.reduce((sum, c) => sum + c.totalGross, 0))} zł <span className="text-primary-foreground/40 text-xs font-normal">~{fmtEur(savedCalcs.reduce((sum, c) => sum + c.totalGross, 0))} €</span></span>
                    </div>
                    <p className="text-primary-foreground/50 text-[10px] leading-snug pt-1">{t("calc.euVatZero")}</p>
                  </div>

                </div>
              )}

              {orderSent ? (
                <div className="text-center space-y-4 py-4">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
                  <h3 className="text-primary-foreground font-bold text-lg">{t("calc.thankYou")}</h3>
                  <p className="text-primary-foreground/50 text-sm">{t("calc.thankYouDesc")}</p>
                  <button
                    onClick={() => { setOrderSent(false); setOrderName(""); setOrderPhone(""); setOrderEmail(""); setOrderNip(""); setOrderStreet(""); setOrderPostal(""); setOrderCity(""); setOrderNotes(""); setCalcFile(null); setSavedCalcs([]); }}
                    className="text-accent text-sm font-semibold hover:underline"
                  >
                    {t("calc.sendNext")}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.nameCompany")}</label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-accent/70" />
                      <input value={orderName} onChange={(e) => { setOrderName(e.target.value); setValidationErrors(prev => { const n = new Set(prev); n.delete("name"); return n; }); }} maxLength={100}
                        className={`w-full bg-primary border rounded-lg pl-10 pr-3 py-3.5 text-primary-foreground text-base outline-none focus:border-accent transition-colors ${validationErrors.has("name") ? "border-destructive ring-1 ring-destructive/50" : "border-primary-foreground/15"}`} />
                    </div>
                  </div>
                  <div>
                    <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.email")}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-accent/70" />
                      <input type="email" value={orderEmail} onChange={(e) => { setOrderEmail(e.target.value); setValidationErrors(prev => { const n = new Set(prev); n.delete("email"); return n; }); }} maxLength={255}
                        className={`w-full bg-primary border rounded-lg pl-10 pr-3 py-3.5 text-primary-foreground text-base outline-none focus:border-accent transition-colors ${validationErrors.has("email") ? "border-destructive ring-1 ring-destructive/50" : "border-primary-foreground/15"}`} />
                    </div>
                  </div>
                  <div>
                    <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.phone")}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-accent/70" />
                      <input type="tel" value={orderPhone} onChange={(e) => setOrderPhone(e.target.value)} maxLength={20} placeholder="np. +48 534 900 620"
                        className="w-full bg-primary border border-primary-foreground/15 rounded-lg pl-10 pr-3 py-3.5 text-primary-foreground text-base placeholder:text-primary-foreground/30 outline-none focus:border-accent" />
                    </div>
                  </div>
                  <div>
                    <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.nip")}</label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-accent/70" />
                      <input value={orderNip} onChange={(e) => setOrderNip(e.target.value)} maxLength={20} placeholder="np. 123-456-78-90"
                        className="w-full bg-primary border border-primary-foreground/15 rounded-lg pl-10 pr-3 py-3.5 text-primary-foreground text-base placeholder:text-primary-foreground/30 outline-none focus:border-accent" />
                    </div>
                  </div>
                  <div>
                    <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.street")}</label>
                    <input value={orderStreet} onChange={(e) => setOrderStreet(e.target.value)} maxLength={200} placeholder="np. ul. Przykładowa 10/2"
                      className="w-full bg-primary border border-primary-foreground/15 rounded-lg px-4 py-3.5 text-primary-foreground text-base placeholder:text-primary-foreground/30 outline-none focus:border-accent" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.postalCode")}</label>
                      <input value={orderPostal} onChange={(e) => setOrderPostal(e.target.value)} maxLength={10} placeholder="00-000"
                        className="w-full bg-primary border border-primary-foreground/15 rounded-lg px-4 py-3.5 text-primary-foreground text-base placeholder:text-primary-foreground/30 outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.city")}</label>
                      <input value={orderCity} onChange={(e) => setOrderCity(e.target.value)} maxLength={100} placeholder="np. Warszawa"
                        className="w-full bg-primary border border-primary-foreground/15 rounded-lg px-4 py-3.5 text-primary-foreground text-base placeholder:text-primary-foreground/30 outline-none focus:border-accent" />
                    </div>
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="text-primary-foreground/60 text-xs font-bold tracking-widest uppercase mb-2 block">{t("calc.notes")}</label>
                    <textarea value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} rows={3} placeholder="Np. kolor tła, dodatkowe wymagania..."
                      className="w-full bg-primary border border-primary-foreground/15 rounded-lg px-4 py-3.5 text-primary-foreground text-base placeholder:text-primary-foreground/30 outline-none focus:border-accent resize-none" />
                  </div>

                  {uploadProgress !== null && (
                    <div className="md:col-span-2 lg:col-span-3 h-2 bg-primary-foreground/10 rounded-full overflow-hidden">
                      <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  )}

                  {showFormHint && (
                    <div className="md:col-span-2 lg:col-span-3 bg-accent/15 border border-accent/30 rounded-xl px-4 py-3 text-center animate-in fade-in slide-in-from-top-2 duration-300">
                      <p className="text-accent font-semibold text-sm">
                        ⬇ Uzupełnij dane powyżej, a następnie kliknij „Wyślij zamówienie"
                      </p>
                    </div>
                  )}
                  <div className="md:col-span-2 lg:col-span-3">
                    <button
                      onClick={handleOrderSubmit}
                      disabled={orderSending || !orderName.trim() || !orderEmail.trim()}
                      className="w-full gradient-gold text-accent-foreground font-black text-lg uppercase tracking-widest rounded-2xl py-4 shadow-gold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {orderSending ? <><Loader2 className="w-5 h-5 animate-spin" />{t("calc.sending")}</> : <><Send className="w-5 h-5" />{shape === "custom" ? t("calc.sendInquiry") : t("calc.sendOrder")}</>}
                    </button>
                    <p className="text-center text-primary-foreground/30 text-xs mt-3">
                      {savedCalcs.length > 0
                        ? `${savedCalcs.length} ${t("calc.itemsNote")}`
                        : t("calc.orderNote")}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <p className="text-center text-primary-foreground/40 text-xs mt-6 tracking-wider">
              {t("calc.footer")}
            </p>
          </div>
        </section>
      </Layout>

      <AlertDialog open={confirmMissingFileOpen} onOpenChange={setConfirmMissingFileOpen}>
        <AlertDialogContent className="bg-background border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calc.missingFileTitle", "Brak załączonego projektu")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calc.missingFileDesc", "Nie załączyłeś pliku z projektem graficznym. Czy na pewno chcesz wysłać zamówienie bez projektu?")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmMissingFileOpen(false)} className="border-border">
              {t("calc.missingFileCancel", "Dodaj plik")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmMissingFile} className="gradient-gold text-accent-foreground">
              {t("calc.missingFileConfirm", "Wyślij mimo to")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={paramsErrorOpen} onOpenChange={setParamsErrorOpen}>
        <AlertDialogContent className="bg-background border-border text-foreground max-w-md">
          <AlertDialogHeader className="items-center text-center">
            <div className="w-14 h-14 rounded-full bg-destructive/15 flex items-center justify-center mb-2">
              <span className="text-destructive text-2xl font-black">!</span>
            </div>
            <AlertDialogTitle className="text-lg">{t("calc.paramsErrorTitle", "Brak wymaganych parametrów")}</AlertDialogTitle>
            <AlertDialogDescription className="text-base leading-relaxed">
              {paramsErrorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="justify-center">
            <AlertDialogAction onClick={() => setParamsErrorOpen(false)} className="gradient-gold text-accent-foreground w-full sm:w-auto">
              {t("calc.paramsErrorAction", "Rozumiem")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="text-accent text-xs font-extrabold tracking-[3px] uppercase">{children}</span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

function ShapeIcon({ shapeKey, active }: { shapeKey: string; active: boolean }) {
  const color = active ? "hsl(var(--accent))" : "hsl(var(--primary-foreground) / 0.4)";
  const stroke = active ? "hsl(var(--accent))" : "hsl(var(--primary-foreground) / 0.25)";
  const size = 44;

  if (shapeKey === "rect") {
    return (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none">
        <rect x="4" y="8" width="36" height="28" rx="3" fill={color} fillOpacity={0.15} stroke={stroke} strokeWidth="2.5" />
        <rect x="4" y="8" width="36" height="28" rx="3" fill={color} fillOpacity={active ? 0.25 : 0.08} />
      </svg>
    );
  }
  if (shapeKey === "round") {
    return (
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none">
        <ellipse cx="22" cy="22" rx="18" ry="16" fill={color} fillOpacity={0.15} stroke={stroke} strokeWidth="2.5" />
        <ellipse cx="22" cy="22" rx="18" ry="16" fill={color} fillOpacity={active ? 0.25 : 0.08} />
      </svg>
    );
  }
  // custom / po obrysie - irregular shape
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" fill="none">
      <path d="M22 4 L38 14 L40 30 L30 40 L14 38 L4 26 L8 12 Z" fill={color} fillOpacity={0.15} stroke={stroke} strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M22 4 L38 14 L40 30 L30 40 L14 38 L4 26 L8 12 Z" fill={color} fillOpacity={active ? 0.25 : 0.08} />
    </svg>
  );
}
